import { api } from '@xtnd/web/shared/api';

export interface ReplicationMetadata {
  tenantId: string;
  dataType: string;
  lastReplication: string | null;
  updatedAt: string | null;
}

export interface ReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  mattersCount: number;
}

export interface MatterSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  matter: MatterData | null;
}

export interface MatterData {
  tenantId: string;
  matterId: string;
  matter?: any;
  status: string;
  clientId?: string;
  clientName?: string;
  displayNumber: string;
  number?: string;
  customNumber?: string;
  description?: string;
  updatedAt: string;
  createdAt: string;
  replicatedAt?: string;
}

export interface SyncMatterResult {
  success: boolean;
  message: string;
  matter: MatterData;
}

export type MatterSearchType = 'matterId' | 'displayNumber' | 'number';

// Contacts types
export interface ContactsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  contactsCount: number;
}

export interface ContactSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  contact: ContactData | null;
}

export interface ContactData {
  tenantId: string;
  contactId: string;
  contact?: any;
  name: string;
  type: string;
  primaryEmailAddress?: string;
  updatedAt: string;
  createdAt: string;
  replicatedAt?: string;
}

export interface SyncContactResult {
  success: boolean;
  message: string;
  contact: ContactData;
}

export type ContactSearchType = 'contactId' | 'name' | 'email';

// Bills types
export interface BillsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  billsCount: number;
}

export interface BillSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  bill: BillData | null;
}

export interface BillData {
  tenantId: string;
  billId: string;
  bill?: any;
  matterId?: string;
  number?: string;
  state: string;
  kind?: string;
  total: number;
  balance: number;
  due?: number;
  paid?: number;
  etag?: string;
  createdAt: string;
  updatedAt: string;
  replicatedAt?: string;
}

export interface SyncBillResult {
  success: boolean;
  message: string;
  bill: BillData;
}

export type BillSearchType = 'billId' | 'billNumber' | 'state';

// Bank Accounts types
export interface BankAccountsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  bankAccountsCount: number;
}

export interface BankAccountSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  bankAccount: BankAccountData | null;
}

export interface BankAccountData {
  tenantId: string;
  bankAccountId: string;
  name: string;
  type: string;
  institution: string;
  accountNumber?: string;
  balance?: number;
  currency?: string;
  currencySymbol?: string;
  bankTransactionsCount?: number;
  defaultAccount?: boolean;
  etag?: string;
  createdAt: string;
  updatedAt: string;
  replicatedAt?: string;
}

export interface SyncBankAccountResult {
  success: boolean;
  message: string;
  bankAccount: BankAccountData;
}

export type BankAccountSearchType = 'bankAccountId' | 'name' | 'type';

// Bank Transactions types
export interface BankTransactionsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  bankTransactionsCount: number;
}

export interface BankTransactionSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  bankTransaction: BankTransactionData | null;
}

export interface BankTransactionData {
  tenantId: string;
  bankTransactionId: string;
  type: string;
  date: string;
  amount: number;
  currency?: string;
  description?: string;
  bankAccountId?: string;
  bankAccountName?: string;
  matterId?: string;
  matterDisplayNumber?: string;
  clientId?: string;
  clientName?: string;
  source?: string;
  etag?: string;
  createdAt: string;
  updatedAt: string;
  replicatedAt?: string;
}

export interface SyncBankTransactionResult {
  success: boolean;
  message: string;
  bankTransaction: BankTransactionData;
}

export type BankTransactionSearchType = 'bankTransactionId' | 'date' | 'type';

// Activities types
export interface ActivitiesReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  activitiesCount: number;
}

export interface ActivitySearchResult {
  found: boolean;
  source?: 'dynamodb' | 'clio' | null;
  activity?: ActivityData | null;
  activities?: ActivityData[];
  count?: number;
  message?: string;
}

export interface ActivityData {
  tenantId?: string;
  activityId?: string;
  id?: string;
  type: string;
  description?: string;
  date: string;
  quantityInHours?: number;
  quantity_in_hours?: number;
  roundedQuantityInHours?: number;
  rounded_quantity_in_hours?: number;
  quantity?: number;
  roundedQuantity?: number;
  rounded_quantity?: number;
  quantityRedacted?: boolean;
  quantity_redacted?: boolean;
  price: number;
  note?: string;
  flatRate?: boolean;
  flat_rate?: boolean;
  billed: boolean;
  onBill?: boolean;
  on_bill?: boolean;
  total: number;
  contingencyFee?: boolean;
  contingency_fee?: boolean;
  nonBillable?: boolean;
  non_billable?: boolean;
  nonBillableTotal?: number;
  non_billable_total?: number;
  noCharge?: boolean;
  no_charge?: boolean;
  taxSetting?: string | null;
  tax_setting?: string | null;
  currency?: string | null;
  userId?: string;
  userEtag?: string;
  matterId?: string;
  matterEtag?: string;
  matter?: any;
  billId?: string;
  billEtag?: string;
  bill?: any;
  activityDescriptionId?: string;
  activityDescriptionEtag?: string;
  activity_description?: any;
  etag?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
  replicatedAt?: string;
}

export interface SyncActivityResult {
  success: boolean;
  message: string;
  activity: ActivityData;
}

export type ActivitySearchType = 'activityId' | 'matterId' | 'billId';

// Activity Descriptions types
export interface ActivityDescriptionsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  activityDescriptionsCount: number;
}

export interface ActivityDescriptionSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  activityDescription: any | null;
}

export interface SyncActivityDescriptionResult {
  success: boolean;
  message: string;
  activityDescription: any;
}

export type ActivityDescriptionSearchType = 'activityDescriptionId' | 'name';

// Expense Categories types
export interface ExpenseCategoriesReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  expenseCategoriesCount: number;
}

export interface ExpenseCategorySearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  expenseCategory: any | null;
}

export interface SyncExpenseCategoryResult {
  success: boolean;
  message: string;
  expenseCategory: any;
}

export type ExpenseCategorySearchType = 'expenseCategoryId' | 'name';

// Tasks types
export interface TasksReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  tasksCount: number;
}

export interface TaskSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  task: any | null;
}

export interface SyncTaskResult {
  success: boolean;
  message: string;
  task: any;
}

export type TaskSearchType = 'taskId' | 'name';

// Users types
export interface UsersReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  usersCount: number;
}

export interface UserSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  user: any | null;
}

export interface SyncUserResult {
  success: boolean;
  message: string;
  user: any;
}

export type UserSearchType = 'userId' | 'email' | 'name';

// Line Items types
export interface LineItemsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  lineItemsCount: number;
}

export interface LineItemSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  lineItem: any | null;
}

export interface SyncLineItemResult {
  success: boolean;
  message: string;
  lineItem: any;
}

export type LineItemSearchType = 'lineItemId' | 'billId' | 'matterId';

// Allocations types
export interface AllocationsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  allocationsCount: number;
}

export interface AllocationSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  allocation: any | null;
}

export interface SyncAllocationResult {
  success: boolean;
  message: string;
  allocation: any;
}

export type AllocationSearchType = 'allocationId' | 'matterId' | 'billId' | 'contactId';

// UTBMS Codes types
export interface UtbmsCodesReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  utbmsCodesCount: number;
}

export interface UtbmsCodeSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  utbmsCode: any | null;
}

export interface SyncUtbmsCodeResult {
  success: boolean;
  message: string;
  utbmsCode: any;
}

export type UtbmsCodeSearchType = 'utbmsCodeId' | 'code';

// UTBMS Sets types
export interface UtbmsSetsReplicationMetadataResponse {
  metadata: ReplicationMetadata;
  utbmsSetsCount: number;
}

export interface UtbmsSetSearchResult {
  found: boolean;
  source: 'dynamodb' | 'clio' | null;
  utbmsSet: any | null;
}

export interface SyncUtbmsSetResult {
  success: boolean;
  message: string;
  utbmsSet: any;
}

export type UtbmsSetSearchType = 'utbmsSetId' | 'name';

class DataReplicationApi {
  private readonly baseUrl = 'clio/replication';

  // ===========================
  // MATTERS METHODS
  // ===========================

  /**
   * Get matters replication metadata including count
   */
  async getMattersReplicationMetadata(): Promise<ReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/matters`);
  }

  /**
   * Update matters replication metadata (reset lastReplication date)
   */
  async updateMattersReplicationMetadata(lastReplication: string | null): Promise<{
    success: boolean;
    metadata: ReplicationMetadata;
  }> {
    return api.put(`${this.baseUrl}/metadata/matters`, { lastReplication });
  }

  /**
   * Get count of replicated matters
   */
  async getMattersCount(): Promise<{ count: number }> {
    return api.get(`${this.baseUrl}/matters/count`);
  }

  /**
   * Search for a matter in DynamoDB (local cache)
   */
  async searchMatterInDynamoDB(
    searchValue: string,
    searchType: MatterSearchType
  ): Promise<MatterSearchResult> {
    return api.get(`${this.baseUrl}/matters/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Search for a matter in Clio (live API)
   */
  async searchMatterInClio(
    searchValue: string,
    searchType: MatterSearchType
  ): Promise<MatterSearchResult> {
    return api.get(`${this.baseUrl}/matters/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Sync (replicate) a specific matter from Clio to DynamoDB
   */
  async syncMatterFromClio(matterId: string): Promise<SyncMatterResult> {
    return api.post(`${this.baseUrl}/matters/${matterId}/sync`);
  }

  // ===========================
  // CONTACTS METHODS
  // ===========================

  /**
   * Get contacts replication metadata including count
   */
  async getContactsReplicationMetadata(): Promise<ContactsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/contacts`);
  }

  /**
   * Update contacts replication metadata (reset lastReplication date)
   */
  async updateContactsReplicationMetadata(lastReplication: string | null): Promise<{
    success: boolean;
    metadata: ReplicationMetadata;
  }> {
    return api.put(`${this.baseUrl}/metadata/contacts`, { lastReplication });
  }

  /**
   * Get count of replicated contacts
   */
  async getContactsCount(): Promise<{ count: number }> {
    return api.get(`${this.baseUrl}/contacts/count`);
  }

  /**
   * Search for a contact in DynamoDB (local cache)
   */
  async searchContactInDynamoDB(
    searchValue: string,
    searchType: ContactSearchType
  ): Promise<ContactSearchResult> {
    return api.get(`${this.baseUrl}/contacts/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Search for a contact in Clio (live API)
   */
  async searchContactInClio(
    searchValue: string,
    searchType: ContactSearchType
  ): Promise<ContactSearchResult> {
    return api.get(`${this.baseUrl}/contacts/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Sync (replicate) a specific contact from Clio to DynamoDB
   */
  async syncContactFromClio(contactId: string): Promise<SyncContactResult> {
    return api.post(`${this.baseUrl}/contacts/${contactId}/sync`);
  }

  // ===========================
  // BILLS METHODS
  // ===========================

  /**
   * Get bills replication metadata including count
   */
  async getBillsReplicationMetadata(): Promise<BillsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/bills`);
  }

  /**
   * Update bills replication metadata (reset lastReplication date)
   */
  async updateBillsReplicationMetadata(lastReplication: string | null): Promise<{
    success: boolean;
    metadata: ReplicationMetadata;
  }> {
    return api.put(`${this.baseUrl}/metadata/bills`, { lastReplication });
  }

  /**
   * Get count of replicated bills
   */
  async getBillsCount(): Promise<{ count: number }> {
    return api.get(`${this.baseUrl}/bills/count`);
  }

  /**
   * Search for a bill in DynamoDB (local cache)
   */
  async searchBillInDynamoDB(
    searchValue: string,
    searchType: BillSearchType
  ): Promise<BillSearchResult> {
    return api.get(`${this.baseUrl}/bills/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Search for a bill in Clio (live API)
   */
  async searchBillInClio(
    searchValue: string,
    searchType: BillSearchType
  ): Promise<BillSearchResult> {
    return api.get(`${this.baseUrl}/bills/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Sync (replicate) a specific bill from Clio to DynamoDB
   */
  async syncBillFromClio(billId: string): Promise<SyncBillResult> {
    return api.post(`${this.baseUrl}/bills/${billId}/sync`);
  }

  // ===========================
  // BANK ACCOUNTS METHODS
  // ===========================

  /**
   * Get bank accounts replication metadata including count
   */
  async getBankAccountsReplicationMetadata(): Promise<BankAccountsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/bank-accounts`);
  }

  /**
   * Update bank accounts replication metadata (reset lastReplication date)
   */
  async updateBankAccountsReplicationMetadata(lastReplication: string | null): Promise<{
    success: boolean;
    metadata: ReplicationMetadata;
  }> {
    return api.put(`${this.baseUrl}/metadata/bank-accounts`, { lastReplication });
  }

  /**
   * Get count of replicated bank accounts
   */
  async getBankAccountsCount(): Promise<{ count: number }> {
    return api.get(`${this.baseUrl}/bank-accounts/count`);
  }

  /**
   * Search for a bank account in DynamoDB (local cache)
   */
  async searchBankAccountInDynamoDB(
    searchValue: string,
    searchType: BankAccountSearchType
  ): Promise<BankAccountSearchResult> {
    return api.get(`${this.baseUrl}/bank-accounts/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Search for a bank account in Clio (live API)
   */
  async searchBankAccountInClio(
    searchValue: string,
    searchType: BankAccountSearchType
  ): Promise<BankAccountSearchResult> {
    return api.get(`${this.baseUrl}/bank-accounts/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Sync (replicate) a specific bank account from Clio to DynamoDB
   */
  async syncBankAccountFromClio(accountId: string): Promise<SyncBankAccountResult> {
    return api.post(`${this.baseUrl}/bank-accounts/${accountId}/sync`);
  }

  // ===========================
  // BANK TRANSACTIONS METHODS
  // ===========================

  /**
   * Get bank transactions replication metadata including count
   */
  async getBankTransactionsReplicationMetadata(): Promise<BankTransactionsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/bank-transactions`);
  }

  /**
   * Update bank transactions replication metadata (reset lastReplication date)
   */
  async updateBankTransactionsReplicationMetadata(lastReplication: string | null): Promise<{
    success: boolean;
    metadata: ReplicationMetadata;
  }> {
    return api.put(`${this.baseUrl}/metadata/bank-transactions`, { lastReplication });
  }

  /**
   * Get count of replicated bank transactions
   */
  async getBankTransactionsCount(): Promise<{ count: number }> {
    return api.get(`${this.baseUrl}/bank-transactions/count`);
  }

  /**
   * Search for a bank transaction in DynamoDB (local cache)
   */
  async searchBankTransactionInDynamoDB(
    searchValue: string,
    searchType: BankTransactionSearchType
  ): Promise<BankTransactionSearchResult> {
    return api.get(`${this.baseUrl}/bank-transactions/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Search for a bank transaction in Clio (live API)
   */
  async searchBankTransactionInClio(
    searchValue: string,
    searchType: BankTransactionSearchType
  ): Promise<BankTransactionSearchResult> {
    return api.get(`${this.baseUrl}/bank-transactions/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Sync (replicate) a specific bank transaction from Clio to DynamoDB
   */
  async syncBankTransactionFromClio(transactionId: string): Promise<SyncBankTransactionResult> {
    return api.post(`${this.baseUrl}/bank-transactions/${transactionId}/sync`);
  }

  /**
   * Preview bank transactions that would be synced for a matter
   */
  async previewMatterBankTransactions(
    matterId: string,
    options?: { dateFrom?: string; dateTo?: string; limit?: number }
  ): Promise<{
    totalNewRecords: number;
    totalExistingRecords: number;
    newTransactions: any[];
    existingTransactions: any[];
    lastFiveLocal: any[];
  }> {
    const params = new URLSearchParams();
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options?.dateTo) params.append('dateTo', options.dateTo);
    if (options?.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/replication/clio/matters/${matterId}/bank-transactions/preview${queryString}`);
  }

  /**
   * Sync bank transactions for a matter
   */
  async syncMatterBankTransactions(
    matterId: string,
    options?: { dateFrom?: string; dateTo?: string; limit?: number }
  ): Promise<{
    success: boolean;
    message: string;
    transactionsFetched: number;
    transactionsSaved: number;
  }> {
    const params = new URLSearchParams();
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options?.dateTo) params.append('dateTo', options.dateTo);
    if (options?.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return api.post(`/replication/clio/matters/${matterId}/bank-transactions/sync${queryString}`);
  }

  /**
   * Preview bank transactions that would be synced for a bank account
   */
  async previewBankAccountTransactions(
    bankAccountId: string,
    options?: { dateFrom?: string; dateTo?: string; matterId?: string; limit?: number }
  ): Promise<{
    totalNewRecords: number;
    totalExistingRecords: number;
    newTransactions: any[];
    existingTransactions: any[];
    lastFiveLocal: any[];
  }> {
    const params = new URLSearchParams();
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options?.dateTo) params.append('dateTo', options.dateTo);
    if (options?.matterId) params.append('matterId', options.matterId);
    if (options?.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/replication/clio/bank-accounts/${bankAccountId}/preview${queryString}`);
  }

  /**
   * Sync bank transactions for a bank account
   */
  async syncBankAccountTransactions(
    bankAccountId: string,
    options?: { dateFrom?: string; dateTo?: string; matterId?: string; limit?: number }
  ): Promise<{
    success: boolean;
    message: string;
    transactionsFetched: number;
    transactionsSaved: number;
  }> {
    const params = new URLSearchParams();
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options?.dateTo) params.append('dateTo', options.dateTo);
    if (options?.matterId) params.append('matterId', options.matterId);
    if (options?.limit) params.append('limit', options.limit.toString());

    const queryString = params.toString() ? `?${params.toString()}` : '';
    return api.post(`/replication/clio/bank-accounts/${bankAccountId}/sync${queryString}`);
  }

  // ===================================
  // ACTIVITIES REPLICATION METHODS
  // ===================================

  /**
   * Get activities replication metadata
   */
  async getActivitiesReplicationMetadata(): Promise<ActivitiesReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/activities`);
  }

  /**
   * Update activities replication metadata (set lastReplication to null to force full sync)
   */
  async updateActivitiesReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; message: string; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/activities`, { lastReplication });
  }

  /**
   * Get activities count
   */
  async getActivitiesCount(): Promise<{ count: number }> {
    return api.get(`${this.baseUrl}/activities/count`);
  }

  /**
   * Search for an activity in DynamoDB
   */
  async searchActivityInDynamoDB(
    searchValue: string,
    searchType: ActivitySearchType
  ): Promise<ActivitySearchResult> {
    return api.get(`${this.baseUrl}/activities/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Search for an activity in Clio
   */
  async searchActivityInClio(
    searchValue: string,
    searchType: ActivitySearchType
  ): Promise<ActivitySearchResult> {
    return api.get(`${this.baseUrl}/activities/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  /**
   * Sync (replicate) a specific activity from Clio to DynamoDB
   */
  async syncActivityFromClio(activityId: string): Promise<SyncActivityResult> {
    return api.post(`${this.baseUrl}/activities/${activityId}/sync`);
  }

  // ===================================
  // ACTIVITY DESCRIPTIONS METHODS
  // ===================================

  async getActivityDescriptionsReplicationMetadata(): Promise<ActivityDescriptionsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/activity-descriptions`);
  }

  async updateActivityDescriptionsReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/activity-descriptions`, { lastReplication });
  }

  async searchActivityDescriptionInDynamoDB(searchValue: string, searchType: ActivityDescriptionSearchType): Promise<ActivityDescriptionSearchResult> {
    return api.get(`${this.baseUrl}/activity-descriptions/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchActivityDescriptionInClio(searchValue: string, searchType: ActivityDescriptionSearchType): Promise<ActivityDescriptionSearchResult> {
    return api.get(`${this.baseUrl}/activity-descriptions/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncActivityDescriptionFromClio(activityDescriptionId: string): Promise<SyncActivityDescriptionResult> {
    return api.post(`${this.baseUrl}/activity-descriptions/${activityDescriptionId}/sync`);
  }

  // ===================================
  // EXPENSE CATEGORIES METHODS
  // ===================================

  async getExpenseCategoriesReplicationMetadata(): Promise<ExpenseCategoriesReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/expense-categories`);
  }

  async updateExpenseCategoriesReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/expense-categories`, { lastReplication });
  }

  async searchExpenseCategoryInDynamoDB(searchValue: string, searchType: ExpenseCategorySearchType): Promise<ExpenseCategorySearchResult> {
    return api.get(`${this.baseUrl}/expense-categories/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchExpenseCategoryInClio(searchValue: string, searchType: ExpenseCategorySearchType): Promise<ExpenseCategorySearchResult> {
    return api.get(`${this.baseUrl}/expense-categories/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncExpenseCategoryFromClio(expenseCategoryId: string): Promise<SyncExpenseCategoryResult> {
    return api.post(`${this.baseUrl}/expense-categories/${expenseCategoryId}/sync`);
  }

  // ===================================
  // TASKS METHODS
  // ===================================

  async getTasksReplicationMetadata(): Promise<TasksReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/tasks`);
  }

  async updateTasksReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/tasks`, { lastReplication });
  }

  async searchTaskInDynamoDB(searchValue: string, searchType: TaskSearchType): Promise<TaskSearchResult> {
    return api.get(`${this.baseUrl}/tasks/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchTaskInClio(searchValue: string, searchType: TaskSearchType): Promise<TaskSearchResult> {
    return api.get(`${this.baseUrl}/tasks/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncTaskFromClio(taskId: string): Promise<SyncTaskResult> {
    return api.post(`${this.baseUrl}/tasks/${taskId}/sync`);
  }

  // ===================================
  // USERS METHODS
  // ===================================

  async getUsersReplicationMetadata(): Promise<UsersReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/users`);
  }

  async updateUsersReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/users`, { lastReplication });
  }

  async searchUserInDynamoDB(searchValue: string, searchType: UserSearchType): Promise<UserSearchResult> {
    return api.get(`${this.baseUrl}/users/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchUserInClio(searchValue: string, searchType: UserSearchType): Promise<UserSearchResult> {
    return api.get(`${this.baseUrl}/users/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncUserFromClio(userId: string): Promise<SyncUserResult> {
    return api.post(`${this.baseUrl}/users/${userId}/sync`);
  }

  // ===================================
  // LINE ITEMS METHODS
  // ===================================

  async getLineItemsReplicationMetadata(): Promise<LineItemsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/line-items`);
  }

  async updateLineItemsReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/line-items`, { lastReplication });
  }

  async searchLineItemInDynamoDB(searchValue: string, searchType: LineItemSearchType): Promise<LineItemSearchResult> {
    return api.get(`${this.baseUrl}/line-items/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchLineItemInClio(searchValue: string, searchType: LineItemSearchType): Promise<LineItemSearchResult> {
    return api.get(`${this.baseUrl}/line-items/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncLineItemFromClio(lineItemId: string): Promise<SyncLineItemResult> {
    return api.post(`${this.baseUrl}/line-items/${lineItemId}/sync`);
  }

  // ===================================
  // ALLOCATIONS METHODS
  // ===================================

  async getAllocationsReplicationMetadata(): Promise<AllocationsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/allocations`);
  }

  async updateAllocationsReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/allocations`, { lastReplication });
  }

  async searchAllocationInDynamoDB(searchValue: string, searchType: AllocationSearchType): Promise<AllocationSearchResult> {
    return api.get(`${this.baseUrl}/allocations/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchAllocationInClio(searchValue: string, searchType: AllocationSearchType): Promise<AllocationSearchResult> {
    return api.get(`${this.baseUrl}/allocations/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncAllocationFromClio(allocationId: string): Promise<SyncAllocationResult> {
    return api.post(`${this.baseUrl}/allocations/${allocationId}/sync`);
  }

  // ===================================
  // UTBMS CODES METHODS
  // ===================================

  async getUtbmsCodesReplicationMetadata(): Promise<UtbmsCodesReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/utbms-codes`);
  }

  async updateUtbmsCodesReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/utbms-codes`, { lastReplication });
  }

  async searchUtbmsCodeInDynamoDB(searchValue: string, searchType: UtbmsCodeSearchType): Promise<UtbmsCodeSearchResult> {
    return api.get(`${this.baseUrl}/utbms-codes/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchUtbmsCodeInClio(searchValue: string, searchType: UtbmsCodeSearchType): Promise<UtbmsCodeSearchResult> {
    return api.get(`${this.baseUrl}/utbms-codes/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncUtbmsCodeFromClio(utbmsCodeId: string): Promise<SyncUtbmsCodeResult> {
    return api.post(`${this.baseUrl}/utbms-codes/${utbmsCodeId}/sync`);
  }

  // ===================================
  // UTBMS SETS METHODS
  // ===================================

  async getUtbmsSetsReplicationMetadata(): Promise<UtbmsSetsReplicationMetadataResponse> {
    return api.get(`${this.baseUrl}/metadata/utbms-sets`);
  }

  async updateUtbmsSetsReplicationMetadata(lastReplication: string | null): Promise<{ success: boolean; metadata: ReplicationMetadata }> {
    return api.put(`${this.baseUrl}/metadata/utbms-sets`, { lastReplication });
  }

  async searchUtbmsSetInDynamoDB(searchValue: string, searchType: UtbmsSetSearchType): Promise<UtbmsSetSearchResult> {
    return api.get(`${this.baseUrl}/utbms-sets/search?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async searchUtbmsSetInClio(searchValue: string, searchType: UtbmsSetSearchType): Promise<UtbmsSetSearchResult> {
    return api.get(`${this.baseUrl}/utbms-sets/search-clio?value=${encodeURIComponent(searchValue)}&type=${searchType}`);
  }

  async syncUtbmsSetFromClio(utbmsSetId: string): Promise<SyncUtbmsSetResult> {
    return api.post(`${this.baseUrl}/utbms-sets/${utbmsSetId}/sync`);
  }
}

export const dataReplicationApi = new DataReplicationApi();

